import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import { apiPost } from '../services/api';
import Sidebar from './Sidebar';
import ThemeToggle from './ThemeToggle';
import GuiaDoSistema from './GuiaDoSistema';
import FaixaDeAvisos from './FaixaDeAvisos';
import PausaDoSistema from './PausaDoSistema';
import RodapeBzR from './RodapeBzR';
import { GUIA_VERSAO } from '../content/guiaDoSistema';

/**
 * Layout para rotas privadas (autenticadas)
 * Inclui Sidebar colapsavel, ThemeToggle, o guia de uso e renderiza o
 * conteudo da rota filha via Outlet
 */
function PrivateLayout() {
  const { darkMode, setDarkMode, sidebarCollapsed, toggleSidebar } = useTheme();
  const { user, updateUser } = useAuth();
  const { selectedCityId } = useSelectedCity();

  // Menu como gaveta no celular. Abaixo de `md` a barra lateral sai do fluxo
  // e entra por cima da página; o conteúdo ocupa a tela inteira. Antes a
  // margem de 260px valia em qualquer largura, e num aparelho de 390px
  // sobrava pouco mais de 100px para a página.
  const [menuAberto, setMenuAberto] = useState(false);
  const fecharMenu = useCallback(() => setMenuAberto(false), []);

  // Gaveta aberta trava a rolagem de trás, senão o dedo arrasta a página
  // por baixo do menu.
  useEffect(() => {
    if (!menuAberto) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = anterior; };
  }, [menuAberto]);

  // Margem dinamica baseada no estado do sidebar (só a partir de md)
  const mainMargin = sidebarCollapsed ? 'md:ml-[80px]' : 'md:ml-[260px]';

  // ── Guia de uso ────────────────────────────────────────────────────
  // Abre sozinho só no primeiro acesso (quem nunca concluiu o guia) e fica
  // disponível no botão "?" para consulta. Reabrir a cada versão nova fazia
  // o guia aparecer de novo para quem já usa o sistema todo dia.
  const precisaVerOGuia = !!user && !user.guia_visto_versao;
  const [guiaAberto, setGuiaAberto] = useState(false);
  const [guiaPorPrimeiroAcesso, setGuiaPorPrimeiroAcesso] = useState(false);

  useEffect(() => {
    if (precisaVerOGuia) {
      setGuiaAberto(true);
      setGuiaPorPrimeiroAcesso(true);
    }
  }, [precisaVerOGuia]);

  const abrirGuia = useCallback(() => {
    setGuiaPorPrimeiroAcesso(false);
    setGuiaAberto(true);
  }, []);

  // Fechar sem concluir não marca nada: o guia obrigatório volta no próximo
  // carregamento. Quem só abriu para consultar continua sem nada a marcar.
  const fecharGuia = useCallback(() => setGuiaAberto(false), []);

  const concluirGuia = useCallback(async () => {
    setGuiaAberto(false);
    if (!precisaVerOGuia) return;

    // Otimista de propósito: o guia não pode reabrir na cara de quem acabou
    // de fechá-lo por causa de uma rede lenta. Se a gravação falhar, ele
    // volta no próximo login — incômodo pequeno perto de travar a tela.
    updateUser({ guia_visto_versao: GUIA_VERSAO });
    try {
      await apiPost('/guia/visto/', { versao: GUIA_VERSAO });
    } catch (err) {
      console.warn('Não foi possível registrar que o guia foi visto:', err);
    }
  }, [precisaVerOGuia, updateUser]);

  return (
    <div
      className={`min-h-screen font-['Inter',sans-serif] ${
        darkMode ? 'text-slate-200' : 'text-slate-700'
      }`}
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)'
      }}
    >
      {/* Sidebar - fixo a esquerda, colapsavel */}
      {/* Fundo escuro atrás da gaveta: dá o alvo de toque para fechar e
          deixa claro que a página está atrás. */}
      {menuAberto && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-950/60"
          onClick={fecharMenu}
          aria-hidden="true"
        />
      )}

      <Sidebar
        darkMode={darkMode}
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        onAbrirGuia={abrirGuia}
        mobileOpen={menuAberto}
        onFecharMobile={fecharMenu}
      />

      {/* Barra superior do celular: é por ela que se abre o menu, já que a
          barra lateral não fica mais visível nessa largura. */}
      <header
        className={`md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b ${darkMode
          ? 'bg-slate-900/95 border-slate-700/40'
          : 'bg-white/95 border-slate-200'}`}
        style={{ backdropFilter: 'blur(8px)' }}
      >
        <button
          type="button"
          onClick={() => setMenuAberto(true)}
          aria-label="Abrir o menu"
          aria-expanded={menuAberto}
          className={`p-2 -ml-2 rounded-lg ${darkMode
            ? 'text-slate-300 hover:bg-slate-800'
            : 'text-slate-600 hover:bg-slate-100'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src="/iconSIDEB.svg" alt="" aria-hidden="true" className="w-7 h-7 object-contain" />
        <img src="/LogoNome.svg" alt="SIDEB" className="h-6 w-auto object-contain" />

        <button
          type="button"
          onClick={abrirGuia}
          aria-label="Abrir o guia rápido do SIDEB"
          className={`ml-auto p-2 -mr-2 rounded-lg ${darkMode
            ? 'text-[#4BA3D8] hover:bg-slate-800'
            : 'text-[#0974BA] hover:bg-slate-100'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </header>

      {/* Botao de alternar tema - fixo no canto superior direito */}
      <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />

      {/* Guia de uso — botao fixo, acima do de tema. Só a partir de md: no
          celular ele fica na barra de cima, porque flutuando cobria os
          cards do rodapé da página. */}
      <button
        type="button"
        onClick={abrirGuia}
        title="Guia rápido do SIDEB"
        aria-label="Abrir o guia rápido do SIDEB"
        className={`hidden md:flex fixed bottom-20 right-5 w-12 h-12 rounded-xl items-center justify-center transition-all duration-300 z-50 ${darkMode
          ? 'bg-slate-800/80 border border-slate-600/15 text-[#4BA3D8] hover:bg-slate-700/80'
          : 'bg-white/80 border border-slate-200 text-[#0974BA] hover:bg-slate-100/80 shadow-lg'
          }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      <GuiaDoSistema
        aberto={guiaAberto}
        onFechar={fecharGuia}
        onConcluir={concluirGuia}
        user={user}
        darkMode={darkMode}
        primeiroAcesso={guiaPorPrimeiroAcesso}
      />

      {/* Conteudo Principal - com margem dinamica para o sidebar */}
      {/* `min-w-0` para o conteudo poder encolher: sem ele, uma tabela larga
          ou um <select> com nome de escola comprido estica a pagina inteira
          e o celular ganha uma rolagem lateral. */}
      <main className={`${mainMargin} min-w-0 p-4 md:p-6 transition-all duration-300`}>
        {/* A chave por cidade remonta a tela quando o admin troca de municipio
            no menu: as buscas de cada pagina refazem com o novo recorte, sem
            que cada uma precise observar a cidade por conta propria. */}
        {/* Avisos da administração (Configurações → Avisos): fora da chave
            por cidade, para não piscar quando o admin troca de município. */}
        {/* Pausa do sistema (aviso marcado como pausa): a tela inteira para
            quem é barrado, uma faixa para o administrador e no aviso prévio. */}
        <PausaDoSistema darkMode={darkMode} />
        <FaixaDeAvisos className="mb-4" />
        <div key={`cidade-${selectedCityId ?? 'todas'}`} className="min-w-0">
          {/* Outlet renderiza o componente da rota filha */}
          <Outlet context={{ darkMode, setDarkMode, abrirGuia }} />
        </div>
        <RodapeBzR darkMode={darkMode} />
      </main>
    </div>
  );
}

export default PrivateLayout;
